import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { generateManagerQuotesExcel, parseManagerQuotesExcel } from '../utils/managerQuotesExcel'
import { createProcurementResultsBulk } from '../../../shared/api/requestItems.api'

type ExcelItem = {
  id: string
  partNumber: string
  brand?: string | null
  quantity: number
  comment?: string | null
}

type RequestLike = {
  client?: { code?: string | null } | null
  requestNumber?: string | null
  items?: Array<{ id: string }>
}

/**
 * Excel-инструменты менеджерских квот (скачать шаблон по выбранным позициям /
 * загрузить заполненный файл). Вынесено из RequestDetailPage — там логика
 * раздувала файл за лимит 500 строк. Хуки вызываются до early-return в
 * RequestDetailPage, поэтому внутри всё через optional chaining.
 */
export function useManagerQuotesExcel(
  requestId: string | undefined,
  request: RequestLike | undefined,
  selectedItemsForExcel: ExcelItem[],
) {
  const queryClient = useQueryClient()
  const [excelBusy, setExcelBusy] = useState<'down' | 'up' | null>(null)
  const [excelMessage, setExcelMessage] = useState<string | null>(null)

  const handleDownloadExcel = useCallback(async () => {
    if (!request || selectedItemsForExcel.length === 0) return
    setExcelBusy('down')
    setExcelMessage(null)
    try {
      await generateManagerQuotesExcel({
        clientCode: request.client?.code ?? null,
        requestNumber: request.requestNumber ?? '—',
        items: selectedItemsForExcel.map((it) => ({
          requestItemId: it.id,
          partNumber: it.partNumber,
          brand: it.brand ?? null,
          quantity: it.quantity,
          comment: it.comment ?? null,
        })),
      })
    } catch (e) {
      setExcelMessage(`Ошибка скачивания: ${(e as Error).message}`)
    } finally {
      setExcelBusy(null)
    }
  }, [selectedItemsForExcel, request])

  const handleUploadExcel = useCallback(
    async (file: File) => {
      setExcelBusy('up')
      setExcelMessage(null)
      try {
        // Квоты разносятся по ID позиций из самого файла, а не по чекбоксам.
        // Допустимый набор — все позиции заявки.
        const allItemIds = (request?.items ?? []).map((i) => i.id)
        const { quotes, rowsWithUnknownId } = await parseManagerQuotesExcel(file, allItemIds)
        if (quotes.length === 0) {
          setExcelMessage(
            rowsWithUnknownId > 0
              ? 'В файле нет ID позиций этой заявки — скачайте шаблон заново'
              : 'В файле нет строк с ценой — нечего загружать',
          )
          return
        }
        const result = await createProcurementResultsBulk(quotes)
        const dupSuffix =
          result.skippedDuplicates > 0 ? `, ${result.skippedDuplicates} дубл. пропущено` : ''
        const lostSuffix =
          rowsWithUnknownId > 0 ? `, ${rowsWithUnknownId} строк без ID пропущено` : ''
        setExcelMessage(`Загружено ${result.created.length} квот${dupSuffix}${lostSuffix}`)
        await queryClient.invalidateQueries({ queryKey: ['requests', requestId] })
        await Promise.all(
          quotes.map((p) =>
            queryClient.invalidateQueries({
              queryKey: ['procurement-results', p.requestItemId],
            }),
          ),
        )
      } catch (e) {
        const msg = axios.isAxiosError(e)
          ? (e.response?.data?.message ?? e.message)
          : (e as Error).message
        setExcelMessage(`Ошибка загрузки: ${msg}`)
      } finally {
        setExcelBusy(null)
        setTimeout(() => setExcelMessage(null), 8000)
      }
    },
    [request, queryClient, requestId],
  )

  return { excelBusy, excelMessage, setExcelMessage, handleDownloadExcel, handleUploadExcel }
}
